import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { Link } from 'react-router-dom'
import { i18n } from '@/locale/i18n'
import {
  fetchCategories,
  selectCategoriesByParentId,
  selectCategoryStatusById,
} from '@/store/categories'
import constants from '@/constants'
import { categoryListUrl, keepQueryString } from '@/urls'
import { Crevron, ArrowStandIcon } from '@/assets/icons'
import ButtonIcon from '@/components/ui/ButtonIcon/ButtonIcon'
import './CategoriesSideMenuNavigation.scss'

const SideMenuCategoryItem = ({
  category,
  activeMarketType,
  currentSubcategoryId,
  expandedByParent,
  onToggleExpand,
  parentCategory,
  parentId,
  onSelect,
  depth = 0,
}) => {
  const children = useSelector(selectCategoriesByParentId(category.id, activeMarketType))
  const childrenStatus = useSelector(selectCategoryStatusById(category.id, activeMarketType))
  const dispatch = useDispatch()
  const isExpanded = expandedByParent[parentId] != null && String(expandedByParent[parentId]) === String(category.id)
  const isActive = String(category.id) === String(currentSubcategoryId)
  const hasChildren = children && children.length > 0

  useEffect(() => {
    if (childrenStatus === constants.IDLE) {
      dispatch(fetchCategories(category.id, activeMarketType))
    }
  }, [childrenStatus, category.id, activeMarketType, dispatch])

  const categoryUrl = useMemo(() => {
    if (category.tree && category.tree.length > 0) {
      return keepQueryString(categoryListUrl(category.tree))
    }
    if (parentCategory) {
      return keepQueryString(categoryListUrl([parentCategory, category]))
    }
    return keepQueryString(categoryListUrl([category]))
  }, [category, parentCategory])

  // Sheet mode: the whole row expands a parent category, and navigation to the parent
  // itself lives in the "All in <category>" row of the opened group. Leaves navigate.
  const rowExpands = Boolean(onSelect) && hasChildren
  // The parent's `children` payload carries no count; it only arrives with this
  // category's own detail request, so hold the badge's place until then.
  const countPending =
    category.open_events_count == null &&
    (childrenStatus === constants.IDLE || childrenStatus === constants.LOADING)
  const countBadge = category.open_events_count != null ? (
    <span className="side-menu__item-count">{category.open_events_count}</span>
  ) : countPending ? (
    <span className="side-menu__item-count side-menu__item-count--pending" aria-hidden="true" />
  ) : null

  const label = (
    <>
      <span className="side-menu__item-title">{category.title}</span>
      {countBadge}
    </>
  )

  return (
    <li className={`side-menu__item ${depth > 0 ? 'side-menu__item--nested' : ''}`}>
      <div className="side-menu__item-row">
        {rowExpands ? (
          <button
            type="button"
            className={`side-menu__item-link side-menu__item-link--button ${
              isActive ? 'side-menu__item-link--active' : ''
            }`}
            aria-expanded={isExpanded}
            onClick={() => onToggleExpand(category.id, parentId)}
          >
            {label}
          </button>
        ) : (
          <Link
            to={categoryUrl}
            className={`side-menu__item-link ${isActive ? 'side-menu__item-link--active' : ''}`}
            onClick={() => {
              if (onSelect) {
                onSelect(category)
                return
              }
              if (hasChildren && !isExpanded) {
                onToggleExpand(category.id, parentId)
              } else if (!hasChildren) {
                onToggleExpand(null, parentId)
              }
            }}
          >
            {label}
          </Link>
        )}
        {hasChildren ? (
          <button
            type="button"
            className={`side-menu__expand-btn ${isExpanded ? 'side-menu__expand-btn--expanded' : ''}`}
            onClick={() => onToggleExpand(category.id, parentId)}
            // In sheet mode the row itself is the control; the chevron stays as an
            // affordance but is not announced or focusable a second time.
            aria-hidden={rowExpands || undefined}
            tabIndex={rowExpands ? -1 : undefined}
            aria-label={rowExpands ? undefined : isExpanded ? 'Collapse' : 'Expand'}
          >
            <Crevron width="10" height="6" fillcolor="var(--color-Neutral-5)" />
          </button>
        ) : (
          <span className="side-menu__expand-placeholder" aria-hidden="true" />
        )}
      </div>

      {isExpanded && hasChildren && (
        <ul className="side-menu__children">
          {onSelect && (
            <li className="side-menu__item">
              <div className="side-menu__item-row">
                <Link
                  to={categoryUrl}
                  className={`side-menu__item-link ${isActive ? 'side-menu__item-link--active' : ''}`}
                  onClick={() => onSelect(category)}
                >
                  <span className="side-menu__item-title">
                    {i18n('markets.category_all_in', { category: category.title })}
                  </span>
                  {countBadge}
                </Link>
                <span className="side-menu__expand-placeholder" aria-hidden="true" />
              </div>
            </li>
          )}
          {children.map(child => (
            <SideMenuCategoryItem
              key={child.id}
              category={child}
              activeMarketType={activeMarketType}
              currentSubcategoryId={currentSubcategoryId}
              expandedByParent={expandedByParent}
              onToggleExpand={onToggleExpand}
              parentCategory={parentCategory}
              parentId={category.id}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

const CategoriesSideMenuNavigation = ({
  categoryId,
  currentSubcategoryId,
  activeMarketType,
  onClose,
  onCollapse,
  onSelect,
  isCollapsible = true,
  showHeader = true,
  title = '',
  parentCategory,
  className = '',
  // Controlled expanded state (lifted to parent for persistence)
  expandedByParent: externalExpanded,
  onExpandedChange,
  // Virtual category mode props (for Trending/New/All)
  items,
  activeItemId,
  onItemSelect,
  isLoading: externalLoading = false,
}) => {
  const dispatch = useDispatch()
  const store = useStore()
  const subcategories = useSelector(selectCategoriesByParentId(categoryId, activeMarketType))
  const categoryStatus = useSelector(selectCategoryStatusById(categoryId, activeMarketType))

  const [internalExpanded, setInternalExpanded] = useState({})
  const expandedByParent = externalExpanded ?? internalExpanded
  const setExpandedByParent = (val) => {
    const next = typeof val === 'function' ? val(expandedByParent) : val
    if (onExpandedChange) onExpandedChange(next)
    else setInternalExpanded(next)
  }

  const isVirtualMode = !!items

  useEffect(() => {
    if (!isVirtualMode && categoryId && categoryStatus === constants.IDLE) {
      dispatch(fetchCategories(categoryId, activeMarketType))
    }
  }, [isVirtualMode, categoryId, categoryStatus, activeMarketType, dispatch])

  useEffect(() => {
    if (!isVirtualMode && subcategories && subcategories.length > 0) {
      const state = store.getState()
      subcategories.forEach(sub => {
        const status = state.categories.tree[activeMarketType]?.status?.[sub.id]
        if (!status || status === constants.IDLE) {
          dispatch(fetchCategories(sub.id, activeMarketType))
        }
      })
    }
  }, [isVirtualMode, subcategories?.length, activeMarketType, dispatch, store])

  // Fetch 2nd-level children for all 1st-level subcategories (only in category mode)
  useEffect(() => {
    if (!isVirtualMode && subcategories && subcategories.length > 0) {
      const state = store.getState()
      subcategories.forEach(sub => {
        const status = state.categories.tree[activeMarketType]?.status?.[sub.id]
        if (!status || status === constants.IDLE) {
          dispatch(fetchCategories(sub.id, activeMarketType))
        }
      })
    }
  }, [isVirtualMode, subcategories?.length, activeMarketType, dispatch, store])

  const handleToggleExpand = (id, parentId) => {
    setExpandedByParent(prev => {
      const next = { ...prev }
      const currentExpanded = next[parentId]
      const isSame = currentExpanded != null && String(currentExpanded) === String(id)
      if (!id || isSame) {
        if (currentExpanded != null) {
          delete next[currentExpanded]
        }
        delete next[parentId]
      } else {
        if (currentExpanded != null) {
          delete next[currentExpanded]
        }
        next[parentId] = id
      }
      return next
    })
  }

  const allCategoryUrl = useMemo(() => {
    if (parentCategory) {
      return keepQueryString(categoryListUrl([parentCategory]))
    }
    return keepQueryString('/markets/')
  }, [parentCategory])

  const isLoading = isVirtualMode ? externalLoading : categoryStatus === constants.LOADING
  const listItems = isVirtualMode ? items : subcategories
  const hasItems = listItems && listItems.length > 0

  const renderSkeleton = () => (
    <div className="side-menu__skeleton">
      {[75, 60, 85, 70, 90, 65].map((w, i) => (
        <div key={i} className="side-menu__skeleton-item">
          <div className="side-menu__skeleton-text" style={{ width: `${w}%` }} />
          <div className="side-menu__skeleton-badge" />
        </div>
      ))}
    </div>
  )

  const renderVirtualItems = () => (
    <ul className="side-menu__list">
      <li className="side-menu__item">
        <div className="side-menu__item-row">
          <button
            className={`side-menu__item-link side-menu__item-link--button ${!activeItemId ? 'side-menu__item-link--active' : ''}`}
            onClick={() => onItemSelect?.(null)}
          >
            <span className="side-menu__item-title">{i18n('markets.category_all')}</span>
          </button>
        </div>
      </li>
      {items.map(item => {
        const identifier = item.slug || item.id
        const isActive = activeItemId === identifier
        return (
          <li key={item.id || item.slug} className="side-menu__item">
            <div className="side-menu__item-row">
              <button
                className={`side-menu__item-link side-menu__item-link--button ${isActive ? 'side-menu__item-link--active' : ''}`}
                onClick={() => onItemSelect?.(identifier)}
              >
                <span className="side-menu__item-title">{item.title || item.name}</span>
                {item.open_events_count != null && (
                  <span className="side-menu__item-count">{item.open_events_count}</span>
                )}
              </button>
            </div>
          </li>
        )
      })}
    </ul>
  )

  const isAllActive = !isVirtualMode && (
    !currentSubcategoryId || (parentCategory && String(parentCategory.id) === String(currentSubcategoryId))
  )

  const renderCategoryItems = () => (
    <ul className="side-menu__list">
      <li className="side-menu__item">
        <div className="side-menu__item-row">
          <Link
            to={allCategoryUrl}
            className={`side-menu__item-link ${isAllActive ? 'side-menu__item-link--active' : ''}`}
            onClick={() => onSelect?.(parentCategory)}
          >
            <span className="side-menu__item-title">{i18n('markets.category_all')}</span>
            {parentCategory?.open_events_count != null && (
              <span className="side-menu__item-count">{parentCategory.open_events_count}</span>
            )}
          </Link>
          <span className="side-menu__expand-placeholder" aria-hidden="true" />
        </div>
      </li>
      {subcategories.map(category => (
        <SideMenuCategoryItem
          key={category.id}
          category={category}
          activeMarketType={activeMarketType}
          currentSubcategoryId={currentSubcategoryId}
          expandedByParent={expandedByParent}
          onToggleExpand={handleToggleExpand}
          parentId={categoryId}
          parentCategory={parentCategory}
          onSelect={onSelect}
        />
      ))}
    </ul>
  )

  return (
    <nav className={`side-menu ${className}`}>
      {showHeader && (
        <div className="side-menu__header">
          <span className="side-menu__title">
            {isVirtualMode ? (title || i18n('filters.categories')) : i18n('filters.categories')}
          </span>
          {onCollapse && (
            <ButtonIcon
              size="medium"
              highlightOnHover
              cursorPointer
              handleOnClick={onCollapse}
              ariaLabel={i18n('filters.toggle_categories')}
            >
              <ArrowStandIcon
                width="16"
                height="16"
                style={{ transform: 'scaleX(-1)' }}
              />
            </ButtonIcon>
          )}
        </div>
      )}

      {isLoading && !hasItems ? (
        renderSkeleton()
      ) : isVirtualMode ? (
        renderVirtualItems()
      ) : (
        renderCategoryItems()
      )}
    </nav>
  )
}

export default CategoriesSideMenuNavigation
